import * as React from 'react';
import * as ReactDOM from 'react-dom';
import GoogleMapReact from 'google-map-react';
import { Input, Tooltip } from 'antd';

const K_WIDTH = 50;
const K_HEIGHT = 50;

const greatPlaceStyle: any = {
	cursor: 'pointer',
	backgroundImage: 'url(/assets/images/map-pin.svg)',
	backgroundRepeat: 'no-repeat',
	backgroundSize: `${K_WIDTH}px ${K_HEIGHT}px`,
	backgroundPosition: '0',
	// initially any map object has left top corner at lat lng coordinates
	// it's on you to set object origin to 0,0 coordinates
	position: 'absolute',
	width: K_WIDTH,
	height: K_HEIGHT,
	left: -15,
	top: -40,
	textAlign: 'center',
	padding: 4
};

interface MapMarkerProps { // TODO: Why is it not used?
	lat: number;
	lng: number;
	text?: string;
}

export const MapMarker: React.SFC<MapMarkerProps> = (props: Readonly<MapMarkerProps>) => (
	<Tooltip title={props.text}>
		<div style={greatPlaceStyle}></div>
	</Tooltip>
);


interface SearchBoxProps {
	mode: ControlMode;
	placeholder: string;
	onPlacesChanged: (value: {
		lat: number;
		lng: number;
	}) => void;
}

export class SearchBox extends React.Component<SearchBoxProps, {}> {
	private searchBox: google.maps.places.SearchBox;

	componentDidMount(): void {
		const input = ReactDOM.findDOMNode(this.refs.input) as HTMLInputElement;
		this.searchBox = new google.maps.places.SearchBox(input);
		this.searchBox.addListener('places_changed', this.onPlacesChanged);
	}

	componentWillUnmount(): void {
		// https://developers.google.com/maps/documentation/javascript/events#removing
		google.maps.event.clearInstanceListeners(this.searchBox);
	}

	private onPlacesChanged = (): void => {
		if (this.props.onPlacesChanged) {
			const places = this.searchBox.getPlaces();
			const place = places[0];
			if (place) {
				this.props.onPlacesChanged(place.geometry.location.toJSON());
			}
		}
	}

	render(): React.ReactNode {
		const { placeholder, mode } = this.props;

		return (
			<Input
				ref="input"
				placeholder={placeholder}
				type="text"
				disabled={mode === 'display'}
			/>
		);
	}
}

interface MapViewProps {
	center: google.maps.LatLngLiteral | google.maps.LatLngLiteral[];
	zoom: number;
	onClick?: (value: google.maps.LatLngLiteral) => any;
	children: React.ReactNode;
	showDirections?: boolean;
	initMap?: (map: google.maps.Map, maps, places: google.maps.LatLngLiteral | google.maps.LatLngLiteral[]) => void;
}

const directionsService = typeof google != "undefined"
	? new google.maps.DirectionsService
	: undefined;

const calculateAndDisplayRoute = (map: google.maps.Map, pointA: google.maps.LatLngLiteral, pointB: google.maps.LatLngLiteral): void => {
	const directionsDisplay = new google.maps.DirectionsRenderer({
		map: map,
		suppressMarkers: true,
	});
	directionsService.route({
		origin: pointA,
		destination: pointB,
		travelMode: google.maps.TravelMode.DRIVING
	}, (response, status) => {
		if (status == google.maps.DirectionsStatus.OK) {
			directionsDisplay.setDirections(response);
		} else {
			window.alert('Directions request failed due to ' + status);
		}
	});
}

// Return map bounds based on list of places
const getMapBounds = (places: google.maps.LatLngLiteral[]): google.maps.LatLngBounds => {
	const bounds = new google.maps.LatLngBounds();
	for (const latLng of places) {
		bounds.extend(latLng);
	}
	return bounds;
};

// Re-center map when resizing the window
const bindResizeListener = (map: google.maps.Map, maps, bounds: google.maps.LatLngBounds): void => {
	maps.event.addDomListenerOnce(map, 'idle', () => {
		maps.event.addDomListener(window, 'resize', () => {
			map.fitBounds(bounds);
		});
	});
};

// Fit map to its bounds after the api is loaded
const apiIsLoaded = (
	map: google.maps.Map,
	maps,
	places: google.maps.LatLngLiteral[],
	showDirections: boolean,
	zoom: number,
	initMap?: (map: google.maps.Map, maps, places: google.maps.LatLngLiteral | google.maps.LatLngLiteral[]) => void,
): void => {
	// Get bounds by our places
	const bounds = getMapBounds(places);
	let initialZoom = false;
	// This is needed to set the zoom after fitbounds, 
	google.maps.event.addListener(map, 'zoom_changed', function () {
		const zoomChangeBoundsListener = google.maps.event.addListener(map, 'bounds_changed', () => {
			if (map.getZoom() > zoom && !initialZoom) {
				// Change max/min zoom here
				map.setZoom(zoom);
				initialZoom = true;
			}
			google.maps.event.removeListener(zoomChangeBoundsListener);
		});
	});

	// Fit map to bounds
	map.fitBounds(bounds);
	// Bind the resize listener
	bindResizeListener(map, maps, bounds);
	if (showDirections && places.length > 1) {
		for (let i = 1; i < places.length; i++) {
			calculateAndDisplayRoute(map, places[i - 1], places[i]);
		}
	}
	if (initMap) {
		initMap(map, maps, places);
	}
};

export const MapView: React.SFC<MapViewProps> = (props: Readonly<MapViewProps>): JSX.Element => {
	const { zoom, onClick, children, showDirections, initMap } = props;
	let { center } = props;
	if (center != null && !Array.isArray(center) && (center.lat == null || center.lng == null)) {
		center = null;
	}

	return (
		<React.Fragment>
			<GoogleMapReact
				bootstrapURLKeys={
					{
						key: 'AIzaSyAoKGYGGe7LKZTa-GJmV9wFACqO8ysSJgw',
						language: 'en',
						libraries: 'places,geometry',
					} as any
				}
				options={{
					styles: [{
						"featureType": "all",
						"elementType": "labels",
						"stylers": [{
							"visibility": "#on"
						}]
					}]
				}}
				center={Array.isArray(center) ? undefined : center}
				zoom={zoom}
				defaultCenter={{ lng: -82.38653898239136, lat: 23.122648000717167 }}
				defaultZoom={14}
				onClick={onClick}
				yesIWantToUseGoogleMapApiInternals={Array.isArray(center)}
				onGoogleApiLoaded={({ map, maps }) => Array.isArray(center)
					? apiIsLoaded(map, maps, center, showDirections, 17, initMap)
					: initMap && initMap(map, maps, center)}
			>
				{children}
			</GoogleMapReact>
		</React.Fragment>
	);
}



// WEBPACK FOOTER //
// ./src/core/ui/map.view.tsx